"use client"

/* /wheel hero object. One SVG (viewBox 1000x1000): rotating face, still rim
   with 32 lamps, still hub, pink pointer at 12 o'clock.

   The spin is driven entirely in JS on requestAnimationFrame, so the motion
   can be a mass, not an easing curve: a heavy start under torque, a short
   cruise, a long run-down under constant friction (speed falls in a straight
   line to zero, which is what a real wheel does), a small overshoot past the
   stop and a damped rock back onto it. The same loop counts separator
   crossings, so the pointer flick, the lamp chase and the audio ticks stay in
   lockstep with what the eye sees. */

import { useCallback, useEffect, useId, useRef, useState } from "react"
import { cn } from "@/lib/utils"
import { prizeById, SEGMENTS, SEGMENT_COUNT } from "@/lib/wheel/prizes"
import { DEFAULT_VFX, type PrizeWheelProps, type WheelVfx } from "./types"
import { wheelAudio } from "./wheel-audio"

/* ---------- Geometry ---------- */

export const SEGMENT_ANGLE = 360 / SEGMENT_COUNT

const CX = 500
const CY = 500
const FACE_R = 410
const HUB_R = 110
const RIM_R = 451
const RIM_W = 82
const BULB_COUNT = 32
const TEXT_END_R = 380
/** Pegs the flapper rides over: one per separator, on the face, near its edge. */
const PEG_R = 396
const PEG_SIZE = 7

/* ---------- Motion profile (ms) ---------- */

/** Torque phase: speed rises from zero along a quadratic. */
const ACCEL_MS = 1500
/** Full speed. */
const CRUISE_MS = 450
/** Friction phase: speed falls in a straight line to zero. */
const DECEL_MS = 6200
/** Past the stop, then rocked back onto it. */
const OVERSHOOT_DEG = 2.2
const SETTLE_MS = 560
const SPIN_MS = ACCEL_MS + CRUISE_MS + DECEL_MS + SETTLE_MS
const REDUCED_SPIN_MS = 900

/* ---------- Lamp programs ----------
   Rest: three attract patterns in rotation (a comet chase, two comets meeting,
   a fill-up round the rim). Spin: all on for the torque kick, then a comet
   chase running with the wheel, one step per peg. Run-down: a spotlight on
   the lamps around the pointer so the eye is where the stop will be. Landing:
   a wave out from the pointer both ways, hold, blink; big prizes blink twice
   more. */
const ATTRACT: { stepMs: number; steps: number }[] = [
  { stepMs: 640, steps: 14 },
  { stepMs: 420, steps: 18 },
  { stepMs: 80, steps: 40 },
]
const KICK_MS = 260
/** Fraction of the friction phase after which the spotlight takes over. */
const SPOTLIGHT_FROM = 0.86
const WAVE_STEP_MS = 36
const HALF = BULB_COUNT / 2
const BIG_PRIZES = new Set(["credit-25", "lineup6", "credit-100", "secret"])
const mod = (n: number, m: number) => ((n % m) + m) % m

/* ---------- Flapper physics ----------
   The blade hangs from its boss on a leaf spring and is pushed by each peg
   as it passes under the tip. While a peg is under the tip the blade is
   lifted by geometry (it has to clear the pin); on release it carries the
   speed the peg gave it and rings on the spring. Wheel angle is clockwise
   positive, so the pegs pass under the tip left to right and push the tip
   right. Lift is kept positive here; a tip hanging below its pivot moves
   right under a negative SVG rotation, so the sign flips when it is drawn.
   Angles in degrees, time in ms. */
/** How far the peg pushes the blade before it clears (degrees of wheel). */
const CONTACT_IN_DEG = 4
/** How long the blade rides the top of the pin after it passes (degrees). */
const CONTACT_OUT_DEG = 1
/** Blade lift needed to clear a pin. */
const CLEAR_DEG = 6
/** Spring: natural frequency and damping ratio. ~120 ms period, rings twice. */
const SPRING_K = 0.0027
const SPRING_C = 0.026
/** Release speed cap so the fastest pegs fling it about 18 degrees, no more. */
const RELEASE_MAX = 1.0
const REST_EPS = 0.02

/** Blade lift forced by the nearest peg at wheel angle r, or 0 when free.
    thetaLast is the last peg past the pointer (0..22.5), the next one sits
    at thetaLast - 22.5. */
function contactLift(r: number): number {
  const thetaLast = mod(r + SEGMENT_ANGLE / 2, SEGMENT_ANGLE)
  const thetaNext = thetaLast - SEGMENT_ANGLE
  if (thetaLast <= CONTACT_OUT_DEG) return CLEAR_DEG
  if (thetaNext >= -CONTACT_IN_DEG) return (CLEAR_DEG * (thetaNext + CONTACT_IN_DEG)) / CONTACT_IN_DEG
  return 0
}

/* Copied verbatim from src/components/arena/v2/primitives.tsx (public/picks-mark.svg). */
const PICKS_MARK_PATH =
  "M24.3435 26.2618C24.559 26.3092 24.6638 26.5545 24.5492 26.7429L22.0537 30.843C21.9764 30.97 21.8221 31.0274 21.6805 30.9818L18.0184 29.8024C17.9448 29.7787 17.8822 29.7292 17.8421 29.6631L14.8521 24.7335C14.7055 24.4918 14.9198 24.1921 15.1959 24.2528L24.3435 26.2618ZM27.5723 21.7849C27.5039 21.8971 27.374 21.9564 27.2444 21.9345L11.6594 19.3037C11.5675 19.2882 11.4868 19.2335 11.4383 19.1539L8.71924 14.6873C8.58521 14.4671 8.75181 14.1866 9.00927 14.199L22.4215 14.8443C22.5176 14.8489 22.6107 14.8103 22.6752 14.739L29.1237 7.62029C29.3108 7.41366 29.1642 7.08301 28.8854 7.08301H10.0811C9.98691 7.08301 9.89743 7.12434 9.83635 7.19609L6.80359 10.7584C6.66169 10.9251 6.39832 10.9043 6.28435 10.7174L0.047105 0.48883C-0.0835217 0.274612 0.0706639 0 0.321567 0H40.2915C40.5425 0 40.6967 0.274685 40.566 0.4889L27.5723 21.7849Z"
/* Mark box is 40.7 x 31; these centre it at the origin before scaling. */
const MARK_OX = -20.35
const MARK_OY = -15.5

/** Point on a circle. Angle is degrees clockwise from 12 o'clock, which is
    how SEGMENTS is ordered. SVG y grows downward, hence the minus on cos. */
function polar(r: number, deg: number): [number, number] {
  const a = (deg * Math.PI) / 180
  return [CX + r * Math.sin(a), CY - r * Math.cos(a)]
}

/** Pie slice for segment i: centred on i*22.5, spanning half a slice each side. */
function slicePath(i: number): string {
  const a0 = i * SEGMENT_ANGLE - SEGMENT_ANGLE / 2
  const a1 = a0 + SEGMENT_ANGLE
  const [x0, y0] = polar(FACE_R, a0)
  const [x1, y1] = polar(FACE_R, a1)
  return `M ${CX} ${CY} L ${x0} ${y0} A ${FACE_R} ${FACE_R} 0 0 1 ${x1} ${y1} Z`
}

/** Arc along the rim between two clock angles (for the specular). */
function arcPath(r: number, from: number, to: number): string {
  const [x0, y0] = polar(r, from)
  const [x1, y1] = polar(r, to)
  return `M ${x0} ${y0} A ${r} ${r} 0 ${to - from > 180 ? 1 : 0} 1 ${x1} ${y1}`
}

/**
 * Rotation (degrees, clockwise positive) that parks segment `index` under the
 * pointer after `turns` full revolutions.
 *
 * Segment i sits at +i*22.5 clockwise from 12 o'clock in wheel space. Rotating
 * the wheel clockwise by r moves that segment to i*22.5 + r; to land it on the
 * pointer (0) we need r = -i*22.5, hence the negative sign. Normalised into
 * [0, 360) so the wheel keeps turning forward: index 0 -> 0, index 1 -> 337.5.
 * `offsetFraction` (-0.5..0.5 of a slice) nudges the stop off dead centre.
 */
export function rotationForIndex(index: number, turns: number, offsetFraction: number): number {
  const landing = (((-(index + offsetFraction) * SEGMENT_ANGLE) % 360) + 360) % 360
  return turns * 360 + landing
}

/** Index of the last separator the pointer has crossed at rotation r. The
    first separator is half a slice (11.25) away from the start position. */
function separatorsPassed(rotation: number): number {
  return Math.floor((rotation + SEGMENT_ANGLE / 2) / SEGMENT_ANGLE)
}

/* ---------- The mass ---------- */

/**
 * Angle travelled at time t (ms) for a spin that must cover `distance`
 * degrees before the rock-back. Returns [angle, speed in deg/ms].
 *
 * Peak speed w follows from the distance: the torque phase covers w*A/2,
 * the cruise w*C, the friction phase w*D/2. The overshoot rides on top of
 * the distance and the settle phase brings it back with a damped cosine,
 * which starts and ends at zero speed so nothing jolts.
 */
function massProfile(distance: number, t: number): [number, number] {
  const full = distance + OVERSHOOT_DEG
  const w = full / (ACCEL_MS / 2 + CRUISE_MS + DECEL_MS / 2)
  if (t <= 0) return [0, 0]
  if (t < ACCEL_MS) {
    const u = t / ACCEL_MS
    return [(w * ACCEL_MS * u * u) / 2, w * u]
  }
  let angle = (w * ACCEL_MS) / 2
  t -= ACCEL_MS
  if (t < CRUISE_MS) return [angle + w * t, w]
  angle += w * CRUISE_MS
  t -= CRUISE_MS
  if (t < DECEL_MS) {
    const u = t / DECEL_MS
    return [angle + w * t * (1 - u / 2), w * (1 - u)]
  }
  angle += (w * DECEL_MS) / 2 // == full
  t -= DECEL_MS
  const u = Math.min(1, t / SETTLE_MS)
  // From +OVERSHOOT back to 0: e^(-5u) * cos(pi*u*1.5) lands near zero at u=1.
  const damp = Math.exp(-5 * u)
  const rock = OVERSHOOT_DEG * damp * Math.cos(Math.PI * 1.5 * u)
  const back = u >= 1 ? 0 : rock
  return [angle - OVERSHOOT_DEG + back, 0]
}

function reducedProfile(distance: number, t: number): [number, number] {
  const u = Math.min(1, t / REDUCED_SPIN_MS)
  const e = 1 - (1 - u) ** 3
  return [distance * e, 0]
}

/* ---------- Segment styling ---------- */

/** Tint drawn over the grey-800 base. Common credits alternate by parity via
    the black scale; free pick, boost and xp carry the pink surface tint; the
    three big ones are solid pink; the sealed pack is the darkest surface. */
export function segmentTint(i: number): string {
  const prize = prizeById(SEGMENTS[i])
  switch (prize.id) {
    case "credit-25":
    case "lineup6":
    case "credit-100":
      return "hsl(var(--primary))"
    case "secret":
      return "hsl(var(--grey-950))"
  }
  if (prize.kind === "free_pick" || prize.kind === "boost" || prize.kind === "xp")
    return "hsl(var(--primary) / 0.16)"
  if (prize.kind === "respin") return "hsl(var(--white-100))"
  return i % 2 === 0 ? "transparent" : "hsl(var(--black-300))"
}

/** The two slices that carry the floor's headline prizes get their own
    light layers, riding with the wheel: sparks drifting hub to rim, a
    breathing edge where they meet the rim, light rays, twinkle lamps with
    star flares. Each layer is a switch. Opacity and translate only. */
const HOT_SEGMENTS = new Set(["credit-100", "lineup6"])
const SPARKS = 12
const RAYS = 7
const TWINKLES = 5

function SegmentVfx({ i, clipId, glowId, vfx }: { i: number; clipId: string; glowId: string; vfx: WheelVfx }) {
  const a = i * SEGMENT_ANGLE
  return (
    <g clipPath={`url(#${clipId})`} className="motion-reduce:hidden">
      {/* Local frame: y runs along the slice, negative toward the rim. */}
      <g transform={`rotate(${a} ${CX} ${CY})`}>
        {vfx.sparks && Array.from({ length: SPARKS }, (_, k) => {
          const x = CX + ((k * 37) % 120) - 60
          const y0 = CY - HUB_R - 40 - ((k * 53) % 90)
          const r = k % 4 === 0 ? 3.4 : k % 4 === 1 ? 2.6 : 1.8
          const fill = k % 3 === 0 ? "hsl(335 83% 82%)" : "hsl(var(--white-0))"
          const delay = -((k * 0.43) % 2.6)
          const dur = 2.2 + (k % 3) * 0.5
          return (
            <g key={k}>
              <circle
                cx={x}
                cy={y0 + 14}
                r={r * 0.55}
                fill={fill}
                fillOpacity={0.5}
                className="wheel-spark"
                style={{ animationDelay: `${delay}s`, animationDuration: `${dur}s` }}
              />
              <circle
                cx={x}
                cy={y0}
                r={r}
                fill={fill}
                className="wheel-spark"
                style={{ animationDelay: `${delay}s`, animationDuration: `${dur}s` }}
              />
            </g>
          )
        })}
      </g>
      {/* Light rays: thin streaks from deep in the slice out to the rim,
          each shimmering on its own clock. */}
      {vfx.rays &&
        Array.from({ length: RAYS }, (_, k) => {
          const ang = a - SEGMENT_ANGLE / 2 + ((k + 0.5) * SEGMENT_ANGLE) / RAYS
          const [x0, y0] = polar(HUB_R + 70 + ((k * 41) % 60), ang)
          const [x1, y1] = polar(FACE_R - 30, ang)
          return (
            <line
              key={k}
              x1={x0}
              y1={y0}
              x2={x1}
              y2={y1}
              stroke="hsl(var(--white-0))"
              strokeWidth={k % 2 === 0 ? 1.6 : 1}
              strokeLinecap="round"
              className="wheel-ray"
              style={{ animationDelay: `${-((k * 0.53) % 2.1)}s` }}
            />
          )
        })}
      {/* The outer arc breathes: a bright edge where the slice meets the rim. */}
      {vfx.edge && (
        <path
          d={arcPath(FACE_R - 4, a - SEGMENT_ANGLE / 2, a + SEGMENT_ANGLE / 2)}
          fill="none"
          stroke="hsl(var(--white-0))"
          strokeWidth={5}
          strokeLinecap="round"
          className="wheel-arcglow"
          style={{ animationDelay: `${(i * 0.9) % 2.2}s` }}
        />
      )}
      {/* Twinkle lamps along the outer arc, each with a four-point flare. */}
      {vfx.flares &&
        Array.from({ length: TWINKLES }, (_, k) => {
          const ang = a - SEGMENT_ANGLE / 2 + ((k + 0.5) * SEGMENT_ANGLE) / TWINKLES
          const [x, y] = polar(FACE_R - 22, ang)
          const flare = k % 2 === 0 ? 22 : 15
          return (
            <g key={k} className="wheel-twinkle" style={{ animationDelay: `${-((k * 0.29) % 1.4)}s` }}>
              <circle cx={x} cy={y} r={16} fill={`url(#${glowId})`} />
              <g transform={`rotate(${ang} ${x} ${y})`}>
                <line x1={x - flare} y1={y} x2={x + flare} y2={y} stroke="hsl(var(--white-0))" strokeOpacity={0.7} strokeWidth={1.2} strokeLinecap="round" />
                <line x1={x} y1={y - flare} x2={x} y2={y + flare} stroke="hsl(var(--white-0))" strokeOpacity={0.7} strokeWidth={1.2} strokeLinecap="round" />
              </g>
              <circle cx={x} cy={y} r={3.2} fill="hsl(var(--white-0))" />
            </g>
          )
        })}
    </g>
  )
}

/** The stop. The winning slice takes a whoosh: a white flash that fades, a
    sweep of light hub to rim, and a burst of sparks flying out to the rim.
    One shot, riding with the wheel, unmounted when it is done. */
const WIN_SPARKS = 16
const WIN_VFX_MS = 1700

function WinWhoosh({ i, clipId, sweepId, big }: { i: number; clipId: string; sweepId: string; big: boolean }) {
  const a = i * SEGMENT_ANGLE
  return (
    <g clipPath={`url(#${clipId})`} className="motion-reduce:hidden">
      <path d={slicePath(i)} fill="hsl(var(--white-0))" className="wheel-win-flash" />
      <g transform={`rotate(${a} ${CX} ${CY})`}>
        <rect
          x={CX - 160}
          y={CY - FACE_R - 120}
          width={320}
          height={FACE_R + 120}
          fill={`url(#${sweepId})`}
          className="wheel-win-sweep"
          style={{ animationIterationCount: big ? 2 : 1 }}
        />
        {Array.from({ length: WIN_SPARKS }, (_, k) => {
          const x = CX + ((k * 29) % 140) - 70
          const y0 = CY - HUB_R - 20 - ((k * 47) % 70)
          const r = k % 3 === 0 ? 3.6 : k % 3 === 1 ? 2.6 : 1.8
          const fill = k % 4 === 0 ? "hsl(335 83% 82%)" : "hsl(var(--white-0))"
          return (
            <g key={k}>
              <circle
                cx={x}
                cy={y0 + 12}
                r={r * 0.55}
                fill={fill}
                fillOpacity={0.5}
                className="wheel-win-spark"
                style={{ animationDelay: `${(k * 23) % 260}ms`, animationDuration: `${700 + (k % 4) * 140}ms` }}
              />
              <circle
                cx={x}
                cy={y0}
                r={r}
                fill={fill}
                className="wheel-win-spark"
                style={{ animationDelay: `${(k * 23) % 260}ms`, animationDuration: `${700 + (k % 4) * 140}ms` }}
              />
            </g>
          )
        })}
      </g>
    </g>
  )
}

/** Labels are set along their own spoke, hung from the rim end. On the left
    half of the wheel that spoke points leftward, so the word would be read
    upside down; those are drawn end for end instead (turned about the same
    rim point, hung from the other end), which puts them in exactly the same
    band reading left to right. */
function labelFlipped(i: number): boolean {
  const a = i * SEGMENT_ANGLE
  return a > 180 && a < 360
}

function labelSize(short: string): number {
  if (short.length >= 7) return 46
  if (short.length >= 5) return 56
  return 64
}

/* ---------- Component ---------- */

type Phase = "idle" | "spinning" | "settled"

export function PrizeWheel({
  vfx = DEFAULT_VFX,
  targetIndex,
  spinKey,
  onSettled,
  sound,
  size,
  className,
}: PrizeWheelProps) {
  const uid = useId()
  const [phase, setPhase] = useState<Phase>("idle")
  const [chase, setChase] = useState(0)
  const [attract, setAttract] = useState(0)
  const [kick, setKick] = useState(false)
  const [spotlight, setSpotlight] = useState(false)
  /** Landing program step; -1 when none is running. */
  const [landStep, setLandStep] = useState(-1)
  /** Winning slice while its whoosh plays; null otherwise. */
  const [winIndex, setWinIndex] = useState<number | null>(null)
  const [reduced, setReduced] = useState(false)

  const faceRef = useRef<SVGGElement>(null)
  const rotationRef = useRef(0)
  const lastSpinKey = useRef(spinKey)
  const rafRef = useRef(0)
  const bladeRef = useRef<SVGGElement>(null)
  /** Blade angle and angular speed (deg, deg/ms). Lives outside React. */
  const bladeRef2 = useRef({ phi: 0, vel: 0, lastT: 0 })
  const tailRafRef = useRef(0)
  const blinkRef = useRef<number[]>([])
  const settledRef = useRef(true)
  const soundRef = useRef(sound)
  soundRef.current = sound
  const onSettledRef = useRef(onSettled)
  onSettledRef.current = onSettled

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
    const sync = () => setReduced(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  const paint = (deg: number) => {
    if (faceRef.current) faceRef.current.style.transform = `rotate(${deg}deg)`
  }

  /** Advance the blade to time now against wheel angle r moving at
      speed deg/ms. Returns true while it is still moving. */
  const stepBlade = (now: number, r: number, speed: number): boolean => {
    const b = bladeRef2.current
    const dt = Math.min(32, b.lastT ? now - b.lastT : 16)
    b.lastT = now
    const lift = contactLift(r)
    if (lift > 0 && lift >= b.phi) {
      // Pushed: the blade follows the pin and takes on its speed.
      b.phi = lift
      b.vel = Math.min(RELEASE_MAX, (CLEAR_DEG / CONTACT_IN_DEG) * speed)
    } else {
      // Free: leaf spring back to home, damped.
      const acc = -SPRING_K * b.phi - SPRING_C * b.vel
      b.vel += acc * dt
      b.phi += b.vel * dt
      if (b.phi < -CLEAR_DEG * 1.5) {
        b.phi = -CLEAR_DEG * 1.5
        b.vel = 0
      }
    }
    if (bladeRef.current) bladeRef.current.style.transform = `rotate(${-b.phi}deg)`
    return lift > 0 || Math.abs(b.phi) > REST_EPS || Math.abs(b.vel) > 0.0005
  }

  /* Attract mode: the three rest patterns take turns while nothing happens. */
  useEffect(() => {
    if (phase !== "idle" || reduced) return
    const program = ATTRACT[attract]
    let steps = 0
    setChase(0)
    const id = window.setInterval(() => {
      steps += 1
      if (steps >= program.steps) {
        setAttract((a) => (a + 1) % ATTRACT.length)
        return
      }
      setChase((c) => c + 1)
    }, program.stepMs)
    return () => window.clearInterval(id)
  }, [phase, reduced, attract])

  const settle = useCallback((index: number) => {
    if (settledRef.current) return
    settledRef.current = true
    cancelAnimationFrame(rafRef.current)
    setPhase("settled")
    setSpotlight(false)
    setWinIndex(index)
    blinkRef.current.push(window.setTimeout(() => setWinIndex(null), WIN_VFX_MS))
    onSettledRef.current(index)
    // Landing: a wave out from the pointer, hold, blink, then back to attract.
    blinkRef.current.forEach((t) => window.clearTimeout(t))
    blinkRef.current = []
    const big = BIG_PRIZES.has(SEGMENTS[index])
    const program: [number, number][] = []
    for (let w = 0; w <= HALF; w++) program.push([w, WAVE_STEP_MS])
    program.push([100, 520], [-1, 120], [100, 240], [-1, 120], [100, big ? 240 : 700])
    if (big) program.push([-1, 120], [100, 240], [-1, 120], [100, 900])
    let at = 0
    for (const [step, ms] of program) {
      blinkRef.current.push(window.setTimeout(() => setLandStep(step), at))
      at += ms
    }
    blinkRef.current.push(
      window.setTimeout(() => {
        setLandStep(-1)
        setAttract(0)
        setPhase("idle")
      }, at)
    )
  }, [])

  useEffect(() => {
    if (spinKey === lastSpinKey.current || targetIndex == null) return
    lastSpinKey.current = spinKey
    if (!settledRef.current) return // a spin is already running

    const from = rotationRef.current
    const turns = 5 + Math.floor(Math.random() * 3)
    const offset = (Math.random() * 2 - 1) * 0.36
    const to = Math.ceil(from / 360) * 360 + rotationForIndex(targetIndex, turns, offset)
    const distance = to - from
    const duration = reduced ? REDUCED_SPIN_MS : SPIN_MS
    const profile = reduced ? reducedProfile : massProfile

    settledRef.current = false
    blinkRef.current.forEach((t) => window.clearTimeout(t))
    blinkRef.current = []
    setLandStep(-1)
    setWinIndex(null)
    setChase(0)
    setKick(true)
    setSpotlight(false)
    blinkRef.current.push(window.setTimeout(() => setKick(false), KICK_MS))
    rotationRef.current = to
    setPhase("spinning")
    if (soundRef.current) wheelAudio.unlock()

    const start = performance.now()
    let passed = separatorsPassed(from)
    const spotlightAt = ACCEL_MS + CRUISE_MS + DECEL_MS * SPOTLIGHT_FROM
    let spot = false
    const frame = (now: number) => {
      const t = now - start
      const [travelled, speed] = profile(distance, t)
      const angle = t >= duration ? to : from + travelled
      paint(angle)
      if (!spot && !reduced && t >= spotlightAt) {
        spot = true
        setSpotlight(true)
      }
      stepBlade(now, angle, speed)
      const crossed = separatorsPassed(angle) - passed
      if (crossed > 0) {
        passed += crossed
        setChase((c) => c + crossed)
        const s = Math.min(1, speed / 0.9)
        if (soundRef.current) wheelAudio.tick(0.25 + 0.75 * s)
      }
      if (t < duration) {
        rafRef.current = requestAnimationFrame(frame)
        return
      }
      settle(targetIndex)
      // The wheel is down; the blade still rings out and, if the last peg
      // stopped under the tip, stays leaning on it.
      const tail = (n: number) => {
        if (stepBlade(n, to, 0)) tailRafRef.current = requestAnimationFrame(tail)
      }
      tailRafRef.current = requestAnimationFrame(tail)
    }
    cancelAnimationFrame(tailRafRef.current)
    bladeRef2.current.lastT = 0
    rafRef.current = requestAnimationFrame(frame)
  }, [spinKey, targetIndex, reduced, settle])

  useEffect(
    () => () => {
      cancelAnimationFrame(rafRef.current)
      cancelAnimationFrame(tailRafRef.current)
      blinkRef.current.forEach((t) => window.clearTimeout(t))
    },
    []
  )

  /** Lamp brightness 0..1 for lamp k under the running program. Lamp 0 is
      at the pointer; k grows clockwise. */
  const lampLevel = (k: number): number => {
    const fromPointer = Math.min(k, BULB_COUNT - k)
    if (phase === "settled") {
      if (landStep < 0) return 0
      if (landStep >= 100) return 1
      if (fromPointer > landStep) return 0
      return fromPointer === landStep ? 1 : 0.55
    }
    if (reduced) return 0
    if (phase === "spinning") {
      if (kick) return 1
      if (spotlight) return fromPointer <= 2 ? 1 : fromPointer <= 4 ? 0.3 : 0
      // Comet running with the wheel (clockwise), one step per peg.
      const d = mod(k - chase, 4)
      return d === 0 ? 1 : d === 3 ? 0.3 : 0
    }
    switch (attract) {
      case 0: {
        const d = mod(k - chase, 4)
        return d === 0 ? 1 : d === 3 ? 0.3 : 0
      }
      case 1: {
        const a = mod(k - chase, 8) === 0
        const b = mod(k + chase, 8) === 0
        return a || b ? 1 : 0
      }
      default: {
        // Fill-up: lamps come on one by one round the rim, the newest brightest.
        const lit = chase % (BULB_COUNT + 8)
        if (k >= lit) return 0
        return k === lit - 1 ? 1 : 0.45
      }
    }
  }

  const shadowId = `${uid}-shadow`
  const specId = `${uid}-spec`
  const rimId = `${uid}-rim`
  const lensId = `${uid}-lens`
  const glowId = `${uid}-glow`
  const bladeId = `${uid}-blade`
  const bossId = `${uid}-boss`
  const [specX0, specY0] = polar(RIM_R, 215)
  const [specX1, specY1] = polar(RIM_R, 325)

  return (
    <div
      className={cn("relative aspect-square w-full select-none", className)}
      style={size ? { width: size, height: size } : undefined}
    >
      <svg viewBox="0 0 1000 1000" className="block h-full w-full" aria-hidden>
        <defs>
          <radialGradient id={shadowId} cx="50%" cy="50%" r="50%">
            <stop offset="0" stopColor="hsl(var(--black-0))" stopOpacity="0" />
            <stop offset="0.8" stopColor="hsl(var(--black-0))" stopOpacity="0" />
            <stop offset="1" stopColor="hsl(var(--black-0))" stopOpacity="0.6" />
          </radialGradient>
          <linearGradient
            id={specId}
            gradientUnits="userSpaceOnUse"
            x1={specX0}
            y1={specY0}
            x2={specX1}
            y2={specY1}
          >
            <stop offset="0" stopColor="hsl(var(--white-0))" stopOpacity="0" />
            <stop offset="0.5" stopColor="hsl(var(--white-0))" stopOpacity="0.22" />
            <stop offset="1" stopColor="hsl(var(--white-0))" stopOpacity="0" />
          </linearGradient>
          {/* Rim: lit from the upper left, falling into shadow lower right. */}
          <linearGradient id={rimId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="hsl(var(--grey-800))" />
            <stop offset="0.5" stopColor="hsl(var(--grey-900))" />
            <stop offset="1" stopColor="hsl(var(--grey-950))" />
          </linearGradient>
          {/* Lamp lens at rest: a glass dome with the highlight off centre. */}
          <radialGradient id={lensId} cx="38%" cy="34%" r="70%">
            <stop offset="0" stopColor="hsl(var(--white-500))" />
            <stop offset="0.55" stopColor="hsl(var(--white-300))" />
            <stop offset="1" stopColor="hsl(var(--white-100))" />
          </radialGradient>
          {/* Lamp lit: white heart, pink cast, falling off into the rim. */}
          <radialGradient id={glowId} cx="50%" cy="50%" r="50%">
            <stop offset="0" stopColor="hsl(var(--white-0))" stopOpacity="1" />
            <stop offset="0.22" stopColor="hsl(var(--white-0))" stopOpacity="0.9" />
            <stop offset="0.4" stopColor="hsl(var(--primary-0))" stopOpacity="0.45" />
            <stop offset="1" stopColor="hsl(var(--primary-0))" stopOpacity="0" />
          </radialGradient>
          {SEGMENTS.map((_, i) => (
            <clipPath key={i} id={`${uid}-clip-${i}`}>
              <path d={slicePath(i)} />
            </clipPath>
          ))}
          {/* Sweep band for the win whoosh, along the local y (radial) axis. */}
          <linearGradient id={`${uid}-sweep`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="hsl(var(--white-0))" stopOpacity="0" />
            <stop offset="0.42" stopColor="hsl(var(--white-0))" stopOpacity="0" />
            <stop offset="0.5" stopColor="hsl(var(--white-0))" stopOpacity="0.55" />
            <stop offset="0.58" stopColor="hsl(var(--white-0))" stopOpacity="0" />
            <stop offset="1" stopColor="hsl(var(--white-0))" stopOpacity="0" />
          </linearGradient>
          {/* Flapper blade: a turned pink piece, lit from the left. */}
          <linearGradient id={bladeId} gradientUnits="userSpaceOnUse" x1="476" y1="0" x2="524" y2="0">
            <stop offset="0" stopColor="hsl(335 83% 30%)" />
            <stop offset="0.38" stopColor="hsl(335 83% 64%)" />
            <stop offset="0.58" stopColor="hsl(var(--primary))" />
            <stop offset="1" stopColor="hsl(335 83% 26%)" />
          </linearGradient>
          {/* Pivot boss: brushed metal, highlight upper left. */}
          <radialGradient id={bossId} cx="32%" cy="28%" r="80%">
            <stop offset="0" stopColor="hsl(var(--white-600))" />
            <stop offset="0.35" stopColor="hsl(var(--grey-600))" />
            <stop offset="0.8" stopColor="hsl(var(--grey-900))" />
            <stop offset="1" stopColor="hsl(var(--grey-950))" />
          </radialGradient>
        </defs>

        {/* 1. Rotating face (transform written by the rAF loop) */}
        <g
          ref={faceRef}
          style={{ transformOrigin: "500px 500px", willChange: "transform" }}
        >
          <circle cx={CX} cy={CY} r={FACE_R} fill="hsl(var(--grey-800))" />
          {SEGMENTS.map((id, i) => {
            const prize = prizeById(id)
            const [tx, ty] = polar(TEXT_END_R, i * SEGMENT_ANGLE)
            return (
              <g key={`${id}-${i}`}>
                <path d={slicePath(i)} fill={segmentTint(i)} />
                {HOT_SEGMENTS.has(id) && (
                  <SegmentVfx i={i} clipId={`${uid}-clip-${i}`} glowId={glowId} vfx={vfx} />
                )}
                {winIndex === i && (
                  <WinWhoosh i={i} clipId={`${uid}-clip-${i}`} sweepId={`${uid}-sweep`} big={BIG_PRIZES.has(id)} />
                )}
                {prize.kind === "secret" ? (
                  <g
                    transform={`translate(${polar(TEXT_END_R - 42, i * SEGMENT_ANGLE).join(" ")}) rotate(${i * SEGMENT_ANGLE - 90}) scale(2) translate(${MARK_OX} ${MARK_OY})`}
                  >
                    <path d={PICKS_MARK_PATH} fill="hsl(var(--white-0))" />
                  </g>
                ) : (
                  <text
                    x={tx}
                    y={ty}
                    transform={`rotate(${i * SEGMENT_ANGLE - 90 + (labelFlipped(i) ? 180 : 0)} ${tx} ${ty})`}
                    textAnchor={labelFlipped(i) ? "start" : "end"}
                    dominantBaseline="central"
                    fill="hsl(var(--white-0))"
                    fontSize={labelSize(prize.short)}
                    fontWeight={900}
                    className="font-open-sauce"
                    style={{ letterSpacing: "-0.02em" }}
                  >
                    {prize.short}
                  </text>
                )}
              </g>
            )
          })}
          {SEGMENTS.map((_, i) => {
            const a = (i + 0.5) * SEGMENT_ANGLE
            const [x0, y0] = polar(HUB_R, a)
            const [x1, y1] = polar(FACE_R, a)
            return (
              <line
                key={i}
                x1={x0}
                y1={y0}
                x2={x1}
                y2={y1}
                stroke="hsl(var(--white-200))"
                strokeWidth={2}
              />
            )
          })}
          {/* Pegs: turned pins standing on the face at every separator. The
              flapper's tip catches each one, which is what the wheel brakes
              against and what the ticks are. */}
          {SEGMENTS.map((_, i) => {
            const [px, py] = polar(PEG_R, (i + 0.5) * SEGMENT_ANGLE)
            return (
              <g key={`peg-${i}`}>
                <circle cx={px + 2} cy={py + 3} r={PEG_SIZE + 1} fill="hsl(var(--black-0))" fillOpacity={0.45} />
                <circle cx={px} cy={py} r={PEG_SIZE} fill={`url(#${bossId})`} stroke="hsl(var(--white-300))" strokeWidth={1} />
              </g>
            )
          })}
        </g>

        {/* 2. Inner shadow from the rim onto the face */}
        <circle cx={CX} cy={CY} r={FACE_R} fill={`url(#${shadowId})`} pointerEvents="none" />

        {/* 3. Rim: a turned ring. Groove at the face edge, the ring itself lit
              from upper left, a bevel hairline at each edge, the pink outer
              line, one specular arc. */}
        <circle cx={CX} cy={CY} r={FACE_R + 4} fill="none" stroke="hsl(var(--black-0))" strokeOpacity={0.7} strokeWidth={8} />
        <circle cx={CX} cy={CY} r={RIM_R} fill="none" stroke={`url(#${rimId})`} strokeWidth={RIM_W} />
        <circle cx={CX} cy={CY} r={FACE_R + 9} fill="none" stroke="hsl(var(--white-200))" strokeWidth={1.5} />
        <circle cx={CX} cy={CY} r={RIM_R + RIM_W / 2 - 3} fill="none" stroke="hsl(var(--white-200))" strokeWidth={1.5} />
        <circle cx={CX} cy={CY} r={496} fill="none" stroke="hsl(var(--primary))" strokeWidth={2} />
        <path d={arcPath(RIM_R + 30, 215, 325)} fill="none" stroke={`url(#${specId})`} strokeWidth={4} />

        {/* 4. Lamps: a socket, a glass lens, and when lit a heart with a cast
              that spills onto the rim. Lamps come on fast and cool down slow. */}
        {Array.from({ length: BULB_COUNT }, (_, k) => {
          const [bx, by] = polar(RIM_R, (k * 360) / BULB_COUNT)
          const level = lampLevel(k)
          const fade = level >= 1 ? "opacity 70ms linear" : level > 0 ? "opacity 220ms ease-out" : "opacity 460ms ease-out"
          return (
            <g key={k}>
              <circle cx={bx} cy={by} r={12} fill="hsl(var(--grey-950))" stroke="hsl(var(--white-100))" strokeWidth={1} />
              <circle cx={bx} cy={by} r={8} fill={`url(#${lensId})`} />
              <circle
                cx={bx}
                cy={by}
                r={26}
                fill={`url(#${glowId})`}
                style={{ opacity: level, transition: fade }}
              />
            </g>
          )
        })}

        {/* 5. Hub (still) */}
        <circle cx={CX} cy={CY} r={HUB_R + 5} fill="hsl(var(--black-0))" fillOpacity={0.55} />
        <circle cx={CX} cy={CY} r={HUB_R} fill="hsl(var(--grey-950))" stroke="hsl(var(--white-300))" strokeWidth={1.5} />
        <g transform={`translate(${CX} ${CY}) scale(2.4) translate(${MARK_OX} ${MARK_OY})`}>
          <path d={PICKS_MARK_PATH} fill="hsl(var(--white-0))" />
        </g>

        {/* 6. Flapper: a blade hung from a pivot boss above the rim on a leaf
              spring. Each peg lifts it as it passes under the tip; on release
              it rings (transform written by the rAF loop). The boss and the
              bracket stay put. */}
        <rect x={488} y={4} width={24} height={40} rx={4} fill="hsl(var(--grey-900))" stroke="hsl(var(--white-100))" strokeWidth={1} />
        {/* Contact shadow of the blade's base on the rim. */}
        <ellipse cx={503} cy={48} rx={26} ry={9} fill="hsl(var(--black-0))" fillOpacity={0.35} />
        <g ref={bladeRef} style={{ transformOrigin: "500px 40px", willChange: "transform" }}>
          {/* Shadow thrown onto the face, offset down-right of the light. */}
          <path
            d="M 480 44 Q 480 30 490 28 L 510 28 Q 520 30 520 44 L 505 108 Q 500 118 495 108 Z"
            fill="hsl(var(--black-0))"
            fillOpacity={0.45}
            transform="translate(6 8)"
          />
          <path
            d="M 480 44 Q 480 30 490 28 L 510 28 Q 520 30 520 44 L 505 108 Q 500 118 495 108 Z"
            fill={`url(#${bladeId})`}
            stroke="hsl(335 83% 18%)"
            strokeWidth={1.5}
            strokeLinejoin="round"
          />
          {/* Lit edge. */}
          <path d="M 487 34 L 496 102" fill="none" stroke="hsl(var(--white-0))" strokeOpacity={0.45} strokeWidth={1.5} strokeLinecap="round" />
          {/* Tip wear: a harder pink where it meets the pegs. */}
          <path d="M 504 98 L 505 108 Q 500 118 495 108 L 496 98 Z" fill="hsl(335 83% 72%)" fillOpacity={0.55} />
          {/* Thickness at the base where the blade leaves the boss. */}
          <path d="M 482 46 Q 500 52 518 46" fill="none" stroke="hsl(var(--white-0))" strokeOpacity={0.22} strokeWidth={1.2} />
        </g>
        {/* Pivot boss with its screw. */}
        <circle cx={500} cy={40} r={19} fill={`url(#${bossId})`} stroke="hsl(var(--white-200))" strokeWidth={1} />
        <circle cx={500} cy={40} r={6} fill="hsl(var(--grey-950))" stroke="hsl(var(--white-100))" strokeWidth={1} />
        <line x1={496} y1={38} x2={504} y2={42} stroke="hsl(var(--white-300))" strokeWidth={1.2} strokeLinecap="round" />
      </svg>
    </div>
  )
}
